import React, { useState } from 'react';
import { Button } from '@fiverr-private/button';
import { LazyComponent } from '@fiverr-private/orca';
import { FilterIcon } from '@fiverr-private/icons';
import { Divider, Stack } from '@fiverr-private/layout_components';
import { Modal, ModalContent, ModalTrigger } from '@fiverr-private/overlay';
import { tracker } from '@fiverr-private/listing_lib';
import type {
    ActiveSideFiltersType,
    FilterCollectionType,
    SelectedFilter as SelectedFilterType,
} from '@fiverr-private/listing_types';
import { useAppContext } from '../../../context/listingsContext';
import { AllFiltersButtonContent } from '../../side_filters/AllFIltersButtonContent/AllFiltersButtonContent';
import { calculateActiveFiltersCount } from '../FiltersDrawer/utils/calculateActiveFiltersCount';
import { applyFilters } from '../FiltersDrawer/utils/applyFilters';
import {
    FILTERS_MODAL_FULL_SCREEN_BREAKPOINT,
    FILTERS_MODAL_SIZE,
    FILTERS_MODAL_TRACKING_SOURCE,
} from './FiltersModal.constants';

const lazyFiltersModalContent = () =>
    import(/* webpackChunkName: 'listings/FiltersModal' */ './FiltersModalContent/FiltersModalContent').then(
        (module) => ({ default: module.FiltersModalContent })
    );

interface FiltersModalProps {
    filterCollections: FilterCollectionType[];
    activeFilters: ActiveSideFiltersType;
    selectedFiltersHierarchy: SelectedFilterType[];
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    numOfResult: number;
}

export const FiltersModal = ({
    filterCollections = [],
    activeFilters,
    selectedFiltersHierarchy,
    isOpen,
    setIsOpen,
    numOfResult,
}: FiltersModalProps) => {
    const { searchUrl, searcherApiPath, dominateSubCategoryId } = useAppContext();
    const [changedFilterCollections, setChangedFilterCollections] = useState(filterCollections);

    const open = () => {
        tracker.trackFilterGroupOpen('all_filters', FILTERS_MODAL_TRACKING_SOURCE);
        setIsOpen(true);
    };

    const close = () => {
        setIsOpen(false);
        setChangedFilterCollections(filterCollections);
    };

    const applyFiltersHandler = () => {
        applyFilters(changedFilterCollections, activeFilters);
        setIsOpen(false);
    };

    const selectedFiltersCount = calculateActiveFiltersCount(selectedFiltersHierarchy);

    return (
        <Modal
            isOpen={isOpen}
            onOpen={open}
            onClose={close}
            fullScreenBreakpoint={FILTERS_MODAL_FULL_SCREEN_BREAKPOINT}
        >
            <ModalTrigger>
                <Stack marginRight="1" gap="4" width="fit-content" height="fit-content">
                    <Button size="lg" variant="outline" intent="secondary" onClick={open}>
                        <FilterIcon color="currentColor" />
                        <AllFiltersButtonContent selectedFiltersCount={selectedFiltersCount} />
                    </Button>
                    <Divider direction="vertical" height="auto" />
                </Stack>
            </ModalTrigger>
            {/* No renderWhenClosed: collapsible content measures zero height inside display:none */}
            <ModalContent closeOnOverlayClick size={FILTERS_MODAL_SIZE}>
                <LazyComponent
                    lazyImport={lazyFiltersModalContent}
                    componentProps={{
                        numOfResult,
                        searchUrl,
                        searcherApiPath,
                        dominateSubCategoryId,
                        changedFilterCollections,
                        setChangedFilterCollections,
                        applyFiltersHandler,
                    }}
                />
            </ModalContent>
        </Modal>
    );
};
